import logger from '../utils/logger';
import { createClient } from '@supabase/supabase-js';

// Configuration Supabase avec fallback pour développement
const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

// Vérifier si Supabase est configuré
const isSupabaseConfigured = 
 supabaseUrl !== 'https://placeholder.supabase.co' && 
 supabaseAnonKey !== 'placeholder-key' &&
 supabaseUrl.includes('.supabase.co');

// SÉCURITÉ: Debug logging (development only, sans exposer les clés)
if ((import.meta as any).env?.DEV) {
 
 if (!isSupabaseConfigured) {
 logger.warn(' Supabase non configuré - utilisation du mode fallback');
 }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
 auth: {
 persistSession: true, // CRITIQUE pour garder la session après fermeture navigateur
 autoRefreshToken: true, // Refresh automatique avant expiration
 detectSessionInUrl: true, // Détecte les tokens dans l'URL (OAuth)
 // CRITIQUE: Utiliser localStorage au lieu des cookies pour partager la session
 // entre clairify.fr et app.clairify.fr (cookies ne marchent pas cross-subdomain)
 storage: typeof window !== 'undefined' ? window.localStorage : undefined,
 storageKey: 'sb-clairefy-auth', // Clé unique partagée entre tous les sous-domaines
 flowType: 'pkce', // Plus sécurisé pour les SPAs
  // DÉSACTIVÉ: Debug spam trop de logs GoTrueClient
  debug: false,
 },
 global: {
 headers: {
 'x-client-info': 'clairefy-web'
 }
 },
 db: {
 schema: 'public'
 },
 realtime: {
 params: {
 eventsPerSecond: 10
 },
 heartbeatIntervalMs: 30000, // Heartbeat toutes les 30s
 reconnectAfterMs: (tries: number) => Math.min(tries * 1000, 30000), // Reconnexion progressive
 }
});

// Types
export interface Profile {
 id: string;
 email: string;
 full_name?: string;
 company_name?: string;
 company_size?: string;
 industry?: string;
 role?: string;
 subscription_tier: 'free' | 'professional' | 'enterprise';
 credits_remaining: number;
 max_monthly_credits: number;
 onboarding_completed: boolean;
 preferences: any;
 created_at: string;
 updated_at: string;
}

export interface DataSource {
 id: string;
 user_id: string;
 name: string;
 type: 'google_sheets' | 'notion' | 'airtable' | 'csv_upload' | 'excel_upload';
 connection_config: any;
 status: 'active' | 'inactive' | 'error' | 'expired';
 last_sync_at?: string;
 metadata: any;
 created_at: string;
}

export interface DataImport {
 id: string;
 user_id: string;
 data_source_id?: string;
 file_name?: string;
 file_path?: string;
 file_size?: number;
 rows_count: number;
 columns_count: number;
 status: 'pending' | 'processing' | 'completed' | 'failed';
 raw_data_preview?: any;
 column_mapping: any;
 data_quality_score?: number;
 created_at: string;
}

export interface AnalysisJob {
 id: string;
 user_id: string;
 import_id: string;
 name: string;
 description?: string;
 status: 'pending' | 'processing' | 'completed' | 'failed';
 progress_percentage: number;
 current_step?: string;
 steps_completed: string[];
 cost_credits: number;
 ai_model_used: string;
 raw_analysis_result?: any;
 created_at: string;
}

export interface Insight {
 id: string;
 analysis_job_id: string;
 user_id: string;
 type: 'summary' | 'recommendation' | 'anomaly' | 'trend' | 'correlation';
 title: string;
 description: string;
 confidence_score?: number;
 impact_level?: 'low' | 'medium' | 'high' | 'critical';
 category?: string;
 actionable_recommendations: any[];
 supporting_data?: any;
 chart_config?: any;
 is_pinned: boolean;
 created_at: string;
}

export interface ChatSession {
 id: string;
 user_id: string;
 name: string;
 description?: string;
 status: 'active' | 'archived';
 message_count: number;
 last_message_at?: string;
 metadata?: any;
 created_at: string;
 updated_at: string;
}

export interface ChatMessage {
 id: string;
 session_id: string;
 user_id: string;
 role: 'user' | 'assistant' | 'system';
 content: string;
 message_type: 'text' | 'data' | 'analysis' | 'insight' | 'error' | 'file';
 metadata?: any;
 created_at: string;
}

export interface UserFile {
 id: string;
 user_id: string;
 session_id?: string;
 file_name: string;
 file_path: string;
 file_size: number;
 file_type: string;
 mime_type: string;
 data_preview?: any;
 row_count?: number;
 column_count?: number;
 status: 'uploaded' | 'processing' | 'analyzed' | 'error';
 tags?: string[];
 created_at: string;
}

// Auth functions
export async function signUp(email: string, password: string, metadata?: any) {
 logger.log(' Supabase: Signing up user', email);
 const { data, error } = await supabase.auth.signUp({
 email,
 password,
 options: {
 data: metadata
 }
 });
 return { data, error };
}

export async function signIn(email: string, password: string) {
 logger.log(' Supabase: Signing in user', email);
 const { data, error } = await supabase.auth.signInWithPassword({
 email,
 password
 });
 return { data, error };
}

export async function signOut() {
 logger.log(' Supabase: Signing out user');
 const { error } = await supabase.auth.signOut();
 return { error };
}

export async function getCurrentUser() {
 const { data: { user } } = await supabase.auth.getUser();
 logger.log(' Supabase: Current user', user?.email);
 return user;
}

// Profile functions with retry logic for cold starts
export async function getProfile(userId: string, retryCount = 0): Promise<Profile | null> {
 const maxRetries = 2;
 logger.log(' [getProfile] START for userId:', userId, `(attempt ${retryCount + 1}/${maxRetries + 1})`);
 const startTime = Date.now();
 
 try {
 logger.log(' [getProfile] Calling supabase.from("profiles").select()...');
 const { data, error } = await supabase
 .from('profiles')
 .select('*')
 .eq('id', userId)
 .single();
 
 const elapsed = Date.now() - startTime;
 logger.log(' [getProfile] Query completed in', elapsed, 'ms');
 
 if (error) {
 logger.error(' [getProfile] Supabase error:', error);

 
 // Si NetworkError et qu'il reste des tentatives, retry après délai
 if (error.message?.includes('NetworkError') && retryCount < maxRetries) {
 const delay = Math.min(1000 * Math.pow(2, retryCount), 3000); // Backoff: 1s, 2s, 3s max
 logger.warn(`[getProfile] NetworkError detected (cold start?), retrying in ${delay}ms...`);
 await new Promise(resolve => setTimeout(resolve, delay));
 return getProfile(userId, retryCount + 1);
 }
 
 throw error;
 }
 
 logger.log(' [getProfile] Profile retrieved:', data?.email || 'NO EMAIL');
 return data;
 } catch (err: any) {
 const elapsed = Date.now() - startTime;
 logger.error(' [getProfile] Exception after', elapsed, 'ms:', err);
 
 // Retry si NetworkError et qu'il reste des tentatives
 if (err?.message?.includes('NetworkError') && retryCount < maxRetries) {
 const delay = Math.min(1000 * Math.pow(2, retryCount), 3000);
 logger.warn(` [getProfile] NetworkError exception (cold start?), retrying in ${delay}ms...`);
 await new Promise(resolve => setTimeout(resolve, delay));
 return getProfile(userId, retryCount + 1);
 }
 
 throw err;
 }
}

export async function updateProfile(userId: string, updates: Partial<Profile>) {
 const { data, error } = await supabase
 .from('profiles')
 .update(updates)
 .eq('id', userId)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

// Data source functions
export async function getDataSources(userId: string): Promise<DataSource[]> {
 const { data, error } = await supabase
 .from('data_sources')
 .select('*')
 .eq('user_id', userId)
 .order('created_at', { ascending: false });
 
 if (error) throw error;
 return data || [];
}

export async function createDataSource(dataSource: Omit<DataSource, 'id' | 'created_at'>) {
 const { data, error } = await supabase
 .from('data_sources')
 .insert(dataSource)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

// Data import functions
export async function createDataImport(dataImport: Omit<DataImport, 'id' | 'created_at'>) {
 const { data, error } = await supabase
 .from('data_imports')
 .insert(dataImport)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

export async function getDataImports(userId: string): Promise<DataImport[]> {
 const { data, error } = await supabase
 .from('data_imports')
 .select(`
 *,
 data_sources(name, type)
 `)
 .eq('user_id', userId)
 .order('created_at', { ascending: false });
 
 if (error) throw error;
 return data || [];
}

export async function updateDataImport(id: string, updates: Partial<DataImport>) {
 const { data, error } = await supabase
 .from('data_imports')
 .update(updates)
 .eq('id', id)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

// Analysis job functions
export async function createAnalysisJob(job: Omit<AnalysisJob, 'id' | 'created_at'>) {
 const { data, error } = await supabase
 .from('analysis_jobs')
 .insert(job)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

export async function getAnalysisJobs(userId: string): Promise<AnalysisJob[]> {
 const { data, error } = await supabase
 .from('analysis_jobs')
 .select(`
 *,
 data_imports(file_name, rows_count, columns_count)
 `)
 .eq('user_id', userId)
 .order('created_at', { ascending: false });
 
 if (error) throw error;
 return data || [];
}

export async function updateAnalysisJob(id: string, updates: Partial<AnalysisJob>) {
 const { data, error } = await supabase
 .from('analysis_jobs')
 .update(updates)
 .eq('id', id)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

// Insight functions
export async function getInsights(userId: string, analysisJobId?: string): Promise<Insight[]> {
 let query = supabase
 .from('insights')
 .select('*')
 .eq('user_id', userId);
 
 if (analysisJobId) {
 query = query.eq('analysis_job_id', analysisJobId);
 }
 
 const { data, error } = await query.order('created_at', { ascending: false });
 
 if (error) throw error;
 return data || [];
}

export async function createInsight(insight: Omit<Insight, 'id' | 'created_at'>) {
 const { data, error } = await supabase
 .from('insights')
 .insert(insight)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

// File upload functions
export async function uploadFile(file: File, userId: string, folder: string = 'uploads') {
 const fileName = `${userId}/${folder}/${Date.now()}-${file.name}`;
 
 const { data, error } = await supabase.storage
 .from('files')
 .upload(fileName, file, {
 cacheControl: '3600',
 upsert: false
 });

 if (error) throw error;
 return data;
}

export async function getFileUrl(path: string) {
 const { data } = supabase.storage
 .from('files')
 .getPublicUrl(path);
 
 return data.publicUrl;
}

// Chat session functions
export async function createChatSession(session: Omit<ChatSession, 'id' | 'created_at' | 'updated_at'>) {
 const { data, error } = await supabase
 .from('chat_sessions')
 .insert(session)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

export async function getChatSessions(userId: string): Promise<ChatSession[]> {
 const { data, error } = await supabase
 .from('chat_sessions')
 .select('*')
 .eq('user_id', userId)
 .eq('status', 'active')
 .order('updated_at', { ascending: false });
 
 if (error) throw error;
 return data || [];
}

export async function updateChatSession(id: string, updates: Partial<ChatSession>) {
 const { data, error } = await supabase
 .from('chat_sessions')
 .update({ ...updates, updated_at: new Date().toISOString() })
 .eq('id', id)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

export async function deleteChatSession(id: string) {
 const { error } = await supabase
 .from('chat_sessions')
 .update({ status: 'archived' })
 .eq('id', id);
 
 if (error) throw error;
}

// Chat message functions
export async function createChatMessage(message: Omit<ChatMessage, 'id' | 'created_at'>) {
 const { data, error } = await supabase
 .from('chat_messages')
 .insert(message)
 .select()
 .single();
 
 if (error) throw error;
 
 // Update session message count and last_message_at
 await supabase.rpc('increment_session_message_count', {
 session_id: message.session_id
 });
 
 return data;
}

export async function getChatMessages(sessionId: string): Promise<ChatMessage[]> {
 const { data, error } = await supabase
 .from('chat_messages')
 .select('*')
 .eq('session_id', sessionId)
 .order('created_at', { ascending: true });
 
 if (error) throw error;
 return data || [];
}

// User file functions
export async function createUserFile(file: Omit<UserFile, 'id' | 'created_at'>) {
 const { data, error } = await supabase
 .from('user_files')
 .insert(file)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

export async function getUserFiles(userId: string, sessionId?: string): Promise<UserFile[]> {
 let query = supabase
 .from('user_files')
 .select('*')
 .eq('user_id', userId)
 .neq('status', 'error');
 
 if (sessionId) {
 query = query.eq('session_id', sessionId);
 }
 
 const { data, error } = await query.order('created_at', { ascending: false });
 
 if (error) throw error;
 return data || [];
}

export async function updateUserFile(id: string, updates: Partial<UserFile>) {
 const { data, error } = await supabase
 .from('user_files')
 .update(updates)
 .eq('id', id)
 .select()
 .single();
 
 if (error) throw error;
 return data;
}

export async function deleteUserFile(id: string) {
 // First delete the file from storage
 const { data: fileData } = await supabase
 .from('user_files')
 .select('file_path')
 .eq('id', id)
 .single();
 
 if (fileData?.file_path) {
 await supabase.storage
 .from('files')
 .remove([fileData.file_path]);
 }
 
 // Then delete the record
 const { error } = await supabase
 .from('user_files')
 .delete()
 .eq('id', id);
 
 if (error) throw error;
}

// SÉCURITÉ: Enhanced file upload with security validation
export async function uploadFileWithProcessing(file: File, userId: string, sessionId?: string) {
 // Valider le fichier AVANT upload
 const { validateFile } = await import('../utils/fileValidation');
 const validation = await validateFile(file);
 
 if (!validation.valid) {
 throw new Error(validation.error || 'Fichier invalide');
 }
 
 const fileName = `${userId}/uploads/${Date.now()}-${file.name}`;
 
 // Upload file to storage
 const { data: uploadData, error: uploadError } = await supabase.storage
 .from('files')
 .upload(fileName, file, {
 cacheControl: '3600',
 upsert: false
 });

 if (uploadError) throw uploadError;
 
 // Process file data if it's CSV or JSON
 let dataPreview = null;
 let rowCount = 0;
 let columnCount = 0;
 
 if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
 const text = await file.text();
 const lines = text.split('\n').filter(line => line.trim());
 rowCount = lines.length - 1; // Exclude header
 columnCount = lines[0]?.split(',').length || 0;
 
 // Store first 10 rows as preview
 dataPreview = lines.slice(0, Math.min(10, lines.length)) as any;
 } else if (file.type === 'application/json' || file.name.endsWith('.json')) {
 const text = await file.text();
 try {
 const jsonData = JSON.parse(text);
 if (Array.isArray(jsonData)) {
 rowCount = jsonData.length;
 columnCount = jsonData.length > 0 ? Object.keys(jsonData[0]).length : 0;
 dataPreview = jsonData.slice(0, Math.min(5, jsonData.length)) as any;
 }
 } catch (e) {
 logger.warn('Failed to parse JSON file:', e);
 }
 }
 
 // Create file record
 const userFile = await createUserFile({
 user_id: userId,
 session_id: sessionId,
 file_name: file.name,
 file_path: fileName,
 file_size: file.size,
 file_type: file.name.split('.').pop() || 'unknown',
 mime_type: file.type,
 data_preview: dataPreview,
 row_count: rowCount,
 column_count: columnCount,
 status: 'uploaded'
 });
 
 return { userFile, uploadData };
}

// Real-time subscriptions
export function subscribeToAnalysisJob(jobId: string, callback: (payload: any) => void) {
 return supabase
 .channel(`analysis_job_${jobId}`)
 .on('postgres_changes', {
 event: 'UPDATE',
 schema: 'public',
 table: 'analysis_jobs',
 filter: `id=eq.${jobId}`
 }, callback)
 .subscribe();
}

export function subscribeToInsights(userId: string, callback: (payload: any) => void) {
 return supabase
 .channel(`insights_${userId}`)
 .on('postgres_changes', {
 event: 'INSERT',
 schema: 'public',
 table: 'insights',
 filter: `user_id=eq.${userId}`
 }, callback)
 .subscribe();
}

export function subscribeToChatMessages(sessionId: string, callback: (payload: any) => void) {
 return supabase
 .channel(`chat_messages_${sessionId}`)
 .on('postgres_changes', {
 event: 'INSERT',
 schema: 'public',
 table: 'chat_messages',
 filter: `session_id=eq.${sessionId}`
 }, callback)
 .subscribe();
}

// NOUVEAU: Subscription temps réel pour les sessions
export function subscribeToChatSessions(userId: string, callback: (payload: any) => void) {
 return supabase
 .channel(`chat_sessions_${userId}`)
 .on('postgres_changes', {
 event: '*', // INSERT, UPDATE, DELETE
 schema: 'public',
 table: 'chat_sessions',
 filter: `user_id=eq.${userId}`
 }, callback)
 .subscribe();
}

// NOUVEAU: Heartbeat pour maintenir la session active
export function startSessionHeartbeat(userId: string) {
 logger.log(' [Heartbeat] Démarrage heartbeat session pour user:', userId);

 const heartbeat = setInterval(async () => {
 try {
 // Ping léger vers Supabase pour maintenir la session
 const { data: session } = await supabase.auth.getSession();

 if (session?.session) {
 // Session toujours active, continuer
 logger.log(' [Heartbeat] Session active');
 } else {
 // Session expirée, tentative de refresh
 logger.warn(' [Heartbeat] Session expirée, tentative refresh...');
 const { data: { session: newSession }, error } = await supabase.auth.refreshSession();

 if (error || !newSession) {
 logger.error(' [Heartbeat] Impossible de refresh session:', error);
 // Ici on pourrait déclencher une reconnexion ou notification
 } else {
 logger.log(' [Heartbeat] Session refreshée avec succès');
 }
 }
 } catch (error) {
 logger.error(' [Heartbeat] Erreur heartbeat:', error);
 }
 }, 4 * 60 * 1000); // Toutes les 4 minutes (avant les 5 min de refresh)

 // Fonction pour arrêter le heartbeat
 const stopHeartbeat = () => {
 logger.log(' [Heartbeat] Arrêt heartbeat');
 clearInterval(heartbeat);
 };

 return stopHeartbeat;
}

// NOUVEAU: Fonction de récupération automatique de session
export async function recoverSession() {
 logger.log(' [Recovery] Tentative récupération session...');

 try {
 // 1. Vérifier session actuelle
 const { data: { session }, error: sessionError } = await supabase.auth.getSession();

 if (session && !sessionError) {
 logger.log(' [Recovery] Session valide trouvée');
 return { success: true, session };
 }

 // 2. Tentative refresh si session existe mais expirée
 const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

 if (refreshedSession && !refreshError) {
 logger.log(' [Recovery] Session refreshée avec succès');
 return { success: true, session: refreshedSession };
 }

 // 3. Échec récupération
 logger.warn(' [Recovery] Impossible de récupérer la session');
 return { success: false, error: refreshError || sessionError };

 } catch (error) {
 logger.error(' [Recovery] Erreur récupération session:', error);
 return { success: false, error };
 }
}

// =====================================================
// FONCTIONS RPC POUR CHAT PERSISTANT
// =====================================================

/**
 * Créer une session de chat (avec fallback)
 */
export async function createChatSessionRPC(title: string): Promise<string> {
 // Si Supabase n'est pas configuré, utiliser directement le fallback
 if (!isSupabaseConfigured) {
 logger.log(' Supabase non configuré, utilisation du fallback direct');
 const sessionId = `session-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
 logger.log(' Session créée (mode fallback):', sessionId);
 return sessionId;
 }

 try {
 // Essayer d'utiliser la fonction RPC Supabase si disponible
 const { data, error } = await supabase.rpc('create_chat_session', {
 p_title: title
 });

 if (error) {
 logger.warn(' RPC create_chat_session failed, using fallback:', error.message);
 throw error;
 }

 logger.log(' Session créée via RPC:', data);
 return data;
 } catch (error) {
 logger.warn(' Using fallback session creation');
 
 // Fallback: créer une session avec l'API standard
 const sessionId = `session-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
 
 try {
 const user = await getCurrentUser();
 const sessionData = {
 id: sessionId,
 user_id: user?.id || 'anonymous',
 name: title,
 description: 'Session créée automatiquement',
 status: 'active' as const,
 message_count: 0,
 metadata: { created_via: 'fallback' }
 };

 await createChatSession(sessionData);
 logger.log(' Session créée via fallback:', sessionId);
 return sessionId;
 } catch (fallbackError) {
 logger.error(' Fallback session creation failed:', fallbackError);
 // Dernière option: retourner juste l'ID généré
 return sessionId;
 }
 }
}

/**
 * Ajouter un message de chat (avec fallback)
 */
export async function addChatMessageRPC(
 sessionId: string,
 role: 'user' | 'assistant' | 'system',
 content: string,
 thinkingContent?: string,
 claudeModel?: string,
 tokensUsed?: number,
 processingTimeMs?: number
): Promise<string> {
 // Si Supabase n'est pas configuré, utiliser directement le fallback
 if (!isSupabaseConfigured) {
 const messageId = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
 logger.log(' Message créé (mode fallback):', messageId);
 return messageId;
 }

 try {
 // Essayer d'utiliser la fonction RPC Supabase si disponible
 const { data, error } = await supabase.rpc('add_chat_message', {
 p_session_id: sessionId,
 p_role: role,
 p_content: content,
 p_thinking_content: thinkingContent || null,
 p_claude_model: claudeModel || null,
 p_tokens_used: tokensUsed || null,
 p_processing_time_ms: processingTimeMs || null
 });

 if (error) {
 logger.warn(' RPC add_chat_message failed, using fallback:', error.message);
 throw error;
 }

 logger.log(' Message ajouté via RPC:', data);
 return data;
 } catch (error) {
 logger.warn(' Using fallback message creation');
 
 // Fallback: créer un message avec l'API standard
 const messageId = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
 
 try {
 const user = await getCurrentUser();
 const messageData = {
 id: messageId,
 session_id: sessionId,
 user_id: user?.id || 'anonymous',
 role,
 content,
 message_type: 'text' as const,
 metadata: { 
 thinking_content: thinkingContent,
 claude_model: claudeModel,
 tokens_used: tokensUsed,
 processing_time_ms: processingTimeMs,
 created_via: 'fallback'
 }
 };

 await createChatMessage(messageData);
 logger.log(' Message ajouté via fallback:', messageId);
 return messageId;
 } catch (fallbackError) {
 logger.error(' Fallback message creation failed:', fallbackError);
 // Dernière option: retourner juste l'ID généré
 return messageId;
 }
 }
}

// =====================================================
// DEEP REPORTS PERSISTANCE
// =====================================================

export interface DeepReport {
 id: string;
 title: string;
 content: string;
 insights_used: any[];
 charts_generated: any[];
 ai_generated: boolean;
 status: 'generating' | 'completed' | 'failed';
 created_at: string;
 updated_at: string;
}

// Helper functions pour récupérer user et session actuels
const getCurrentUserId = async () => {
 const { data: { user } } = await supabase.auth.getUser();
 return user?.id || null;
};

const getCurrentSessionId = () => {
 // Récupérer depuis localStorage ou contexte
 return localStorage.getItem('clairify_last_session_id') || null;
};

// Sauvegarder un Deep Report généré par IA
export const saveDeepReportRPC = async (
 title: string,
 content: string,
 insights: any[] = [],
 charts: any[] = [],
 metadata: any = {},
 sessionId?: string | null
): Promise<string | null> => {
 if (!isSupabaseConfigured) {
 logger.warn(' Supabase not configured, using localStorage fallback');
 const reportId = `deep-report-${Date.now()}`;
 const reportData = {
 id: reportId,
 title,
 content,
 insights_used: insights,
 charts_generated: charts,
 metadata,
 ai_generated: true,
 status: 'completed',
 created_at: new Date().toISOString(),
 updated_at: new Date().toISOString()
 };
 localStorage.setItem(`deep-report-${reportId}`, JSON.stringify(reportData));
 return reportId;
 }

 try {
 logger.log(' Saving Deep Report to Supabase...', { title, insights: insights.length, charts: charts.length });
 
 const userId = await getCurrentUserId();
 const currentSessionId = sessionId || getCurrentSessionId();
 
 const { data, error } = await supabase.rpc('save_deep_report', {
 p_title: title,
 p_content: content,
 p_insights_used: insights as any,
 p_charts_generated: charts as any,
 p_session_id: currentSessionId,
 p_user_id: userId
 });
 
 if (error) {
 logger.error(' Deep Report save error:', error);
 // Fallback to localStorage
 const reportId = `deep-report-fallback-${Date.now()}`;
 const reportData = { id: reportId, title, content, insights_used: insights, charts_generated: charts };
 localStorage.setItem(`deep-report-${reportId}`, JSON.stringify(reportData));
 return reportId;
 }
 
 logger.log(' Deep Report saved successfully:', data);
 return data;
 
 } catch (error) {
 logger.error(' Deep Report save failed:', error);
 // Fallback to localStorage
 const reportId = `deep-report-error-${Date.now()}`;
 const reportData = { id: reportId, title, content, insights_used: insights, charts_generated: charts };
 localStorage.setItem(`deep-report-${reportId}`, JSON.stringify(reportData));
 return reportId;
 }
};

// Récupérer les Deep Reports d'une session spécifique
export const getSessionDeepReports = async (sessionId: string): Promise<DeepReport[]> => {
 if (!isSupabaseConfigured) {
 logger.warn(' Supabase not configured, returning empty array');
 return [];
 }

 try {
 const userId = await getCurrentUserId();
 if (!userId) return [];

 const { data, error } = await supabase
 .from('deep_reports')
 .select('*')
 .eq('session_id', sessionId)
 .eq('user_id', userId)
 .order('created_at', { ascending: false });

 if (error) {
 logger.error(' Error fetching session deep reports:', error);
 return [];
 }

 logger.log(` Deep Reports chargés pour session ${sessionId}:`, data?.length || 0);
 return data || [];
 } catch (error) {
 logger.error(' Error in getSessionDeepReports:', error);
 return [];
 }
};

// Récupérer le dernier Deep Report d'une session
export const getLatestSessionDeepReport = async (sessionId: string): Promise<DeepReport | null> => {
 const reports = await getSessionDeepReports(sessionId);
 return reports.length > 0 ? reports[0] : null;
};

// Récupérer les Deep Reports de l'utilisateur
export const getUserDeepReports = async (limit: number = 10): Promise<DeepReport[]> => {
 if (!isSupabaseConfigured) {
 logger.warn(' Supabase not configured, using localStorage fallback');
 const reports: DeepReport[] = [];
 for (let i = 0; i < localStorage.length; i++) {
 const key = localStorage.key(i);
 if (key?.startsWith('deep-report-')) {
 try {
 // SÉCURITÉ #25: Parse sécurisé avec validation structure
 const raw = localStorage.getItem(key);
 if (!raw) continue;
 
 const reportData = JSON.parse(raw);
 
 // Valider structure minimale pour éviter prototype pollution
 if (reportData && 
 typeof reportData === 'object' && 
 !Array.isArray(reportData) &&
 reportData.id && 
 typeof reportData.id === 'string') {
 reports.push(reportData);
 } else {
 logger.warn('Invalid report structure, skipping:', key);
 localStorage.removeItem(key); // Nettoyer data corrompue
 }
 } catch (e) {
 logger.warn('Invalid localStorage report:', key);
 localStorage.removeItem(key); // Nettoyer data invalide
 }
 }
 }
 return reports.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, limit);
 }

 try {
 logger.log(' Loading Deep Reports from Supabase...');
 
 const { data, error } = await supabase.rpc('get_user_deep_reports', {
 limit_count: limit
 });
 
 if (error) {
 logger.error(' Deep Reports load error:', error);
 return [];
 }
 
 logger.log(' Deep Reports loaded:', data?.length || 0);
 return data || [];
 
 } catch (error) {
 logger.error(' Deep Reports load failed:', error);
 return [];
 }
};